import React, { useState, useMemo, useCallback } from "react";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { Button, Row, Col, Typography, Checkbox, Tooltip } from "antd";
import { connect } from "dva";
import GoogleMapReact from "google-map-react";
import moment from "moment";
import { AvailableNowOptions, dateStringToStandardFormat } from "@/utils/dateFormatter";
import {
  IncompleteStatusMap,
  ShipmentStatus,
  DealerExchangeType,
  LoadStatusMap,
  LoadStatus,
  TrailerTypeMap,
} from "@/utils/enum";
import { middlePoint } from "@/utils/map";
import { formatDistance } from "@/utils/utils";
import LiveMarker from "@/components/MapMarkers/LiveMarker/LiveMarker";
import ShipmentStatusLabel from "@/components/ShipmentStatusLabel/ShipmentStatusLabel";
import StatusSteps from "@/components/StatusSteps";
import LoadStatusButton from "../../LoadStatusButton";

const { StatusStep } = StatusSteps;
const { Title } = Typography;

const getAvailabilityText = shipmentData =>
  dateStringToStandardFormat(shipmentData?.availableAt, {
    availableNowModifier: shipmentData?.availableNow
      ? AvailableNowOptions.IsAvailableNow
      : AvailableNowOptions.NotAvailableNow,
  });

const getVisibilityTooltip = (canToggle, isVisible) => {
  if (!canToggle) {
    return "Shipment is not currently visible to transporters for self-dispatch and can't set to visible because it's part of an ordered dealer exchange and the other leg isn't delivered yet";
  }
  return isVisible
    ? "Shipment is currently visible to transporters for self-dispatch"
    : "Shipment is not currently visible to transporters for self-dispatch";
};

const ShipmentStatusTracker = ({
  adminShipmentView,
  isDamaged,
  handleToggleDamaged,
  shipmentExpedited,
  visible,
  problemRide,
  directBrokerage,
  dealerExchangeRequired,
  exchangeShipment,
  handleToggleAutosledExpedited,
  handleToggleVisible,
  handleToggleProblemRideChange,
  handleToggleDirectBrokerage,
  handleToggleDealerExchangeRequired,
  configPerm,
  loadingIsDamaged,
  refreshData,
}) => {
  const [showMap, setShowMap] = useState(false);

  const adminShipmentData = useMemo(() => adminShipmentView?.data, [adminShipmentView]);

  const adminShipmentLoadData = useMemo(() => adminShipmentView?.loads, [adminShipmentView]);

  const canToggleVisibility = useMemo(() => {
    if (
      // If there's no dealer exchange
      !exchangeShipment ||
      // or the dealer exchange is unordered
      adminShipmentData.dealerExchangeType === DealerExchangeType.Unordered ||
      // or the current shipment is the parent and the exchange is the child
      adminShipmentData.parentShipmentId === null
    ) {
      return true;
    }

    return exchangeShipment.status === ShipmentStatus.Delivered;
  }, [adminShipmentData, exchangeShipment]);

  const extraMapRenders = useCallback(
    ({ map, maps }) => {
      const directionsService = new maps.DirectionsService();
      const directionsRender = new maps.DirectionsRenderer();
      directionsRender.setMap(map);
      directionsService.route(
        {
          origin: `${adminShipmentData.pickupAddress.latitude},${adminShipmentData.pickupAddress.longitude}`,
          destination: `${adminShipmentData.dropOffAddress.latitude},${adminShipmentData.dropOffAddress.longitude}`,
          travelMode: "DRIVING",
        },
        (response, status) => {
          if (status === "OK") {
            directionsRender.setDirections(response);
          } else {
            window.alert(`Directions request failed due to ${status}`);
          }
        },
      );
    },
    [adminShipmentData],
  );

  if (!adminShipmentData) {
    return null;
  }

  return (
    <div
      style={{
        marginLeft: 20,
        marginBottom: 16,
        border: "1px solid rgba(192, 192, 192, 0.5)",
        background: "#fff",
        borderRadius: 10,
        padding: 16,
        boxShadow: "5px 5px 20px 1px rgb(0 0 0 / 5%)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <section style={{ display: "flex", gap: 8, flexDirection: "column" }}>
          <Title level={4} style={{ padding: 0, margin: "0 0 0 8px" }}>
            Shipment Distance: {formatDistance(adminShipmentData.distance)} miles
          </Title>
          <span style={{ marginLeft: 8 }}>Availability: {getAvailabilityText(adminShipmentData)}</span>
          <span style={{ marginLeft: 8 }}>Trailer Type: {TrailerTypeMap[adminShipmentData?.trailerType]}</span>
        </section>
        <Row gutter={8}>
          <Col flex={1}>
            <div>Shipment Status</div>

            <ShipmentStatusLabel status={adminShipmentData?.status} />
          </Col>
          <Col flex={1}>
            <div>Load Status</div>
            <div style={{ fontWeight: 600 }}>
              {adminShipmentData?.load?.status === LoadStatus.Delivered &&
              configPerm["CoreService.AdminShipments.UpdateStatus"] ? (
                <LoadStatusButton
                  status={adminShipmentData?.load?.status}
                  loadId={adminShipmentData?.load?.id}
                  refreshData={refreshData}
                />
              ) : (
                <span>{LoadStatusMap[adminShipmentData?.load?.status] ?? "-"}</span>
              )}
            </div>
          </Col>
        </Row>
      </div>
      <div style={{ margin: "32px 0" }}>
        <StatusSteps current={adminShipmentData.status}>
          <StatusStep
            step={ShipmentStatus.Draft}
            title="Created Shipment"
            activeBackgroundColor="#42a5fc"
            color="#fff"
            toolTipDescription={moment
              .utc(adminShipmentData.creationTime)
              .locale("en")
              .local()
              .format("MM/DD/YYYY, h:mm A")}
            overrideActive={adminShipmentData.status === ShipmentStatus.Incomplete ? false : undefined}
          />
          <StatusStep
            step={ShipmentStatus.Pending}
            title={adminShipmentData.status === ShipmentStatus.Pending ? "Pending Quotes" : "Quoted"}
            activeBackgroundColor="#2d89e1"
            overrideActive={adminShipmentData.status === ShipmentStatus.Incomplete ? false : undefined}
          />
          <StatusStep
            step={ShipmentStatus.Posted}
            title="Posted"
            activeBackgroundColor="#1c73cb"
            toolTipDescription={
              adminShipmentData.postedAt && adminShipmentData.status >= ShipmentStatus.Posted
                ? dateStringToStandardFormat(adminShipmentData.postedAt, { showTime: true })
                : null
            }
            overrideActive={adminShipmentData.status === ShipmentStatus.Incomplete ? false : undefined}
          />
          <StatusStep
            step={ShipmentStatus.Assigned}
            title="Assign/In-Transit"
            activeBackgroundColor="#065db5"
            overrideActive={adminShipmentData.status === ShipmentStatus.Incomplete ? false : undefined}
          />
          <StatusStep
            step={ShipmentStatus.Delivered}
            title="Delivered"
            activeBackgroundColor="#23D737"
            color="#fff"
            toolTipDescription={
              adminShipmentData.status === ShipmentStatus.Delivered && adminShipmentData?.dropoff?.dropOffTime
                ? dateStringToStandardFormat(adminShipmentData.dropoff.dropOffTime, { showTime: true })
                : null
            }
            overrideActive={adminShipmentData.status === ShipmentStatus.Incomplete ? false : undefined}
          />
          <StatusStep
            step={ShipmentStatus.Incomplete}
            title="Incomplete"
            activeBackgroundColor="#C81038"
            toolTipDescription={
              adminShipmentData.status === ShipmentStatus.Assigned
                ? IncompleteStatusMap[adminShipmentData.incompleteStatus]
                : null
            }
          />
        </StatusSteps>
      </div>
      <div
        style={{
          display: "flex",
          flex: 1,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button type="link" icon={showMap ? <MinusOutlined /> : <PlusOutlined />} onClick={() => setShowMap(!showMap)}>
          Show Map
        </Button>

        <div>
          {adminShipmentData.status >= ShipmentStatus.Delivered &&
            configPerm["CoreService.AdminShipments.ToggleIsDamaged"] && (
              <Tooltip color="#fff" title="A shipment with a vehicle which has been damaged while in transit.">
                <Checkbox checked={isDamaged} onChange={handleToggleDamaged} disabled={loadingIsDamaged}>
                  Damaged in Transit
                </Checkbox>
              </Tooltip>
            )}
          {adminShipmentData.status >= ShipmentStatus.Draft && (
            <>
              {configPerm["CoreService.AdminShipments.ToggleExpedited"] && (
                <Tooltip
                  color="#fff"
                  title="A shipment has either been sitting too long, had multiple cancellations,
                    or a customer offers us more money to get it to them faster, so we artificially
                    increase the price."
                >
                  <Checkbox checked={shipmentExpedited} onChange={handleToggleAutosledExpedited}>
                    Autosled Expedited
                  </Checkbox>
                </Tooltip>
              )}
              {configPerm["CoreService.AdminShipments.ToggleDirectBrokerage"] && (
                <Tooltip color="#fff" title="">
                  <Checkbox checked={directBrokerage} onChange={handleToggleDirectBrokerage}>
                    Direct Brokerage
                  </Checkbox>
                </Tooltip>
              )}
              {configPerm["CoreService.AdminShipments.ToggleExpedited"] &&
                adminShipmentData.dealerExchangeType === DealerExchangeType.Ordered && (
                  <Tooltip color="#fff" title="Transporters must accept both the legs of an Ordered Dealer Exchange.">
                    <Checkbox
                      disabled={adminShipmentData.status >= ShipmentStatus.Assigned}
                      checked={dealerExchangeRequired}
                      onChange={handleToggleDealerExchangeRequired}
                    >
                      Dealer Exchange Required
                    </Checkbox>
                  </Tooltip>
                )}
            </>
          )}
          {adminShipmentData.status >= ShipmentStatus.Posted && (
            <>
              {configPerm["CoreService.AdminShipments.ToggleVisible"] &&
                adminShipmentData.status === ShipmentStatus.Posted && (
                  <Tooltip color="#fff" title={getVisibilityTooltip(canToggleVisibility, visible)}>
                    <Checkbox checked={visible} disabled={!canToggleVisibility} onChange={handleToggleVisible}>
                      Visible to Transporters
                    </Checkbox>
                  </Tooltip>
                )}
              {configPerm["CoreService.AdminShipments.ToggleProblemRide"] && (
                <Tooltip
                  color="#fff"
                  title="A shipment had an out-of-the-ordinary event such as a wreck, stolen vehicle,
                    vehicle mistakenly picked up leading to an unexpected split load, etc."
                >
                  <Checkbox checked={problemRide} onChange={handleToggleProblemRideChange}>
                    Problem Ride
                  </Checkbox>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </div>
      {showMap && (
        <div
          style={{
            height: 400,
            borderRadius: 10,
          }}
        >
          <GoogleMapReact
            bootstrapURLKeys={{ key: GOOGLE_API_KEY }}
            defaultCenter={middlePoint(adminShipmentData.pickupAddress, adminShipmentData.dropOffAddress)}
            defaultZoom={10}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={extraMapRenders}
          >
            {(adminShipmentLoadData || []).map((load, index) => (
              <LiveMarker
                key={index}
                status={adminShipmentData.status === ShipmentStatus.Assigned ? "Assigned" : "Posted"}
              />
            ))}
          </GoogleMapReact>
        </div>
      )}
    </div>
  );
};

export default connect(({ adminShipmentView, configs, loading }) => ({
  adminShipmentView,
  configPerm: configs.configPerm,
  loadingIsDamaged: loading.effects["adminShipmentView/setShipmentAsDamaged"],
}))(ShipmentStatusTracker);
